import { defineStore } from "pinia";
import {
  InsightsAIDreGateway,
  InsightsAIDreGatewayDi,
  TAvaliarInsightResponse
} from "../services/InsightsAIDreGateway";
import { IToastNotification } from "@/core/composables/notification/types";
import { useExportarRelatorioStore } from "@/features/financas/widgets/ExportarRelatorioDRE/useExportarRelatorioStore";
import { TInsightsDreTermoDeUso } from "./types";
import {
  TInsightsDreRelatorioState,
  useInsightsDreRelatorio
} from "./modules/InsightsDreRelatorio";
import {
  TAnaliseInteligenteInsightsStore,
  useInsightsDreAnaliseInteligente
} from "./modules/InsightsDreAnaliseInteligente";
import useNotification from "@/core/composables/notification/useNotification";
import { diContainer } from "@/inversify.config";
import { EInsightsIAAnalysisType } from "../types";

export const iDAgenteDreStore = "iDAgenteDreStore";

export type TAgenteDeDreInsigthsStoreState = {
  loading: boolean;
  isInitialLoading: boolean;
  loadingGerarRelatorio: boolean;
  hasOptin: boolean;
  ativado: boolean;
  termoDeUso: TInsightsDreTermoDeUso | undefined;
  diaGeracaoRelatorio: number | undefined;
  idModeloDRE: string | undefined;
  processando: boolean;
  analiseInteligente: TAnaliseInteligenteInsightsStore;
  relatorio: TInsightsDreRelatorioState;
  notificacao: IToastNotification<string>;
  gateway: InsightsAIDreGateway;
  mensagensLoading: string[];
  mensagemShow: string;
};

const insightsDreAnaliseInteligente = useInsightsDreAnaliseInteligente();
const insightsDreRelatorio = useInsightsDreRelatorio();
const notificacao = useNotification();

const mensagensLoading = [
  "Organizando as informações financeiras do seu grupo...",
  "Organizando as informações financeiras do seu grupo...",
  "Buscando gastos fora do padrão e pontos de atenção...",
  "Calculando riscos e impactos para os próximos meses...",
  "Calculando riscos e impactos para os próximos meses...",
  "Preparando os insights personalizados...",
  "Preparando os insights personalizados...",
  "Tudo pronto! Seus insights já estão disponíveis."
];

export const useAgenteDeDreInsigthsStore = defineStore(iDAgenteDreStore, {
  state: (): TAgenteDeDreInsigthsStoreState => ({
    loading: false,
    isInitialLoading: false,
    loadingGerarRelatorio: false,
    hasOptin: false,
    ativado: false,
    notificacao: notificacao,
    termoDeUso: undefined,
    diaGeracaoRelatorio: undefined,
    idModeloDRE: undefined,
    processando: false,
    analiseInteligente:
      useInsightsDreAnaliseInteligente().analiseInteligenteState(),
    relatorio: useInsightsDreRelatorio().relatorioState(),
    gateway: diContainer.get<InsightsAIDreGateway>(InsightsAIDreGatewayDi),
    mensagensLoading: mensagensLoading,
    mensagemShow: ""
  }),
  actions: {
    setLoading(loading: boolean) {
      this.loading = loading;
    },
    setIsInitialLoading(isInitialLoading: boolean) {
      this.isInitialLoading = isInitialLoading;
    },
    setLoadingGerarRelatorio(loading: boolean) {
      this.loadingGerarRelatorio = loading;
    },
    reset() {
      this.$reset();
    },
    setHasOptin(hasOptin: boolean) {
      this.hasOptin = hasOptin;
    },
    setProcessando(processando: boolean) {
      this.processando = processando;
    },
    setTermoDeUso(termoDeUso: TInsightsDreTermoDeUso) {
      this.termoDeUso = termoDeUso;
    },
    setDiaGeracaoRelatorio(diaGeracaoRelatorio: number) {
      this.diaGeracaoRelatorio = diaGeracaoRelatorio;
    },
    setIdModeloDRE(idModeloDRE: string) {
      this.idModeloDRE = idModeloDRE;
    },
    setAtivado(ativado: boolean) {
      this.ativado = ativado;
    },
    setMensagemShow(mensagemShow: string) {
      this.mensagemShow = mensagemShow;
    },
    ...insightsDreRelatorio,
    ...insightsDreAnaliseInteligente,
    async fetchGerarRelatorio() {
      try {
        this.setLoadingGerarRelatorio(true);

        const result = await this.gateway.gerarRelatorio();

        if (result.isSuccess) {
          const relatorio = result.getValue();

          this.setAtivado(true);
          this.setProcessando(relatorio.primeiraGeracao || false);

          if (!relatorio.primeiraGeracao) {
            await this.fetchGetAgenteDeDre();
          }

          return;
        }

        if (!result.isSuccess) {
          if (import.meta.env.DEV && result.error) {
            this.notificacao.showNotification(result.error, {
              type: "error"
            });
          } else {
            this.notificacao.showNotification(
              "Houve um erro ao obter relatório de insights",
              {
                type: "error"
              }
            );
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoadingGerarRelatorio(false);
      }
    },
    async fetchBuscarRelatorio() {
      try {
        this.setLoading(true);

        const exportarRelatorioStore = useExportarRelatorioStore();

        const resumoDoGrupo =
          this.analiseInteligente.filtros.IdDaEmpresa === "resumo-do-grupo"
            ? true
            : false;

        if (
          !this.analiseInteligente.filtros.IdDaEmpresa ||
          !this.analiseInteligente.filtros.DataDeCompetencia
        ) {
          console.warn(
            "Não foi possível obter o relatorio sem empresa e período"
          );

          return;
        }

        const result = await this.gateway.agenteDeDreFiltros({
          IdDaEmpresa: resumoDoGrupo
            ? ""
            : this.analiseInteligente.filtros.IdDaEmpresa,
          DataDeCompetencia: this.analiseInteligente.filtros.DataDeCompetencia,
          analysisType: this.analiseInteligente.filtros.analysisType,
          analysisTypeModel: this.analiseInteligente.filtros.analysisTypeModel,
          ResumoDoGrupo: resumoDoGrupo
        });

        if (result.isFailure) {
          this.resetRelatorio(this.relatorio);

          return;
        }

        const resultado = result.getValue();

        const dadosRelatorio: TAvaliarInsightResponse | undefined =
          resultado.result;

        if (
          resultado.code !== 200 ||
          (resultado.errors?.length && resultado.errors.length > 0)
        ) {
          this.resetRelatorio(this.relatorio);
          this.notificacao.showNotification(
            resultado.errors?.[0]?.message || "Erro ao obter relatorio",
            {
              type: "error"
            }
          );

          return;
        }

        if (dadosRelatorio) {
          this.setInsights(this.relatorio, dadosRelatorio.analises || []);

          this.setSummaryItems(this.relatorio, this.relatorio.insights.length);

          this.setSecondarySummaryItems(
            this.relatorio,
            dadosRelatorio.criticos || 0,
            dadosRelatorio.atencao || 0,
            dadosRelatorio.infos || 0
          );

          this.setDadosAgenteDeDre(this.relatorio, {
            quantidadeInsights: dadosRelatorio.quantidadeInsights,
            criticos: dadosRelatorio.criticos || 0,
            atencao: dadosRelatorio.atencao || 0,
            infos: dadosRelatorio.infos || 0
          });

          exportarRelatorioStore.setDados({
            competenciaDe: dadosRelatorio.competenciaDe || "",
            competenciaAte: dadosRelatorio.competenciaAte || "",
            regimeDeCompetencia: dadosRelatorio.analiseVertical || false,
            modelo: dadosRelatorio.idModelo || ""
          });
        } else {
          this.resetRelatorio(this.relatorio);
          this.notificacao.showNotification("Erro ao obter relatorio", {
            type: "error"
          });
        }
      } catch (error) {
        console.error(error);
        this.resetRelatorio(this.relatorio);
      } finally {
        this.setLoading(false);
      }
    },
    async fetchObterConfiguracao() {
      try {
        this.setLoading(true);

        const result = await this.gateway.obterConfiguracao();

        if (result.isFailure) {
          this.notificacao.showNotification(
            result.error || "Erro ao obter configuração",
            {
              type: "error"
            }
          );

          this.setLoading(false);

          return;
        }

        const configuracao = result.getValue();

        this.ativado = configuracao.agenteLigado || false;
        this.diaGeracaoRelatorio = configuracao.diaGeracaoRelatorio;
        this.processando = configuracao.primeiraGeracao || false;
        this.idModeloDRE = configuracao.idModeloDRE;

        if (this.ativado && !this.processando) await this.fetchGetAgenteDeDre();
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoading(false);
      }
    },
    async fetchVerificarOptIn() {
      try {
        this.setLoading(true);

        const result = await this.gateway.verificar({
          versaoTermo: this.termoDeUso?.versao || ""
        });

        if (result.isSuccess) {
          const aceitouOptin = result.getValue().termoAceito || false;

          this.setHasOptin(aceitouOptin);

          this.setTermoDeUso({
            url: result.getValue().UrlDocumentoTermo || "",
            versao: result.getValue().versaoTermo || ""
          });

          return true;
        } else {
          if (result.error) {
            this.notificacao.showNotification(result.error, {
              type: "error"
            });
          } else {
            this.notificacao.showNotification("Erro ao verificar opt in", {
              type: "error"
            });
          }

          return false;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoading(false);
      }
    },
    async fetchGetAgenteDeDre() {
      try {
        this.setLoading(true);

        const result = await this.gateway.agenteDeDre();

        if (result.isSuccess) {
          this.setEmpresasOptions(this.analiseInteligente, result.getValue());

          this.setPeriodosOptions(this.analiseInteligente, result.getValue());

          if (this.ativado && !this.processando) {
            // TODO: Remover o setTimeout E DEFINIR ESSE FLUXO
            const idEmpresaInicial =
              (this.analiseInteligente.empresasOptions[0]?.value as string) ||
              "";

            this.setFiltros(this.analiseInteligente, {
              IdDaEmpresa: idEmpresaInicial,
              DataDeCompetencia:
                (this.analiseInteligente.periodosOptions[0]?.value as string) ||
                "",
              analysisType: EInsightsIAAnalysisType.Vertical,
              analysisTypeModel: EInsightsIAAnalysisType.Vertical
            });

            this.fetchBuscarRelatorio();
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoading(false);
      }
    }
  },
  getters: {
    analysisOptions: () => insightsDreAnaliseInteligente.getAnalysisOptions()
  }
});
